import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function Quote() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    designation: '',
    projectType: '',
    projectLocation: '',
    projectSize: '',
    timeline: '',
    budget: '',
    chemicalType: '',
    applicationArea: '',
    specificRequirements: '',
    technicalSpecs: '',
    quantity: '',
    deliveryDate: '',
    previousSupplier: '',
    certificationNeeds: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('https://readdy.ai/api/form/submit/construction-quote-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          designation: '',
          projectType: '',
          projectLocation: '',
          projectSize: '',
          timeline: '',
          budget: '',
          chemicalType: '',
          applicationArea: '',
          specificRequirements: '',
          technicalSpecs: '',
          quantity: '',
          deliveryDate: '',
          previousSupplier: '',
          certificationNeeds: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#7E887E' }}>
              Get Your Custom Quote
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Tell us about your project requirements and get a detailed quote for our construction chemical solutions
            </p>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Benefits */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="text-center p-6 bg-white rounded-lg shadow-md">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(126, 136, 126, 0.1)' }}>
                  <i className="ri-time-line text-2xl" style={{ color: '#7E887E' }}></i>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#7E887E' }}>Quick Response</h3>
                <p className="text-gray-600 text-sm">Get your detailed quote within 24 hours</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-lg shadow-md">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(126, 136, 126, 0.1)' }}>
                  <i className="ri-shield-check-line text-2xl" style={{ color: '#7E887E' }}></i>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#7E887E' }}>Expert Consultation</h3>
                <p className="text-gray-600 text-sm">Free technical consultation included</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-lg shadow-md">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(126, 136, 126, 0.1)' }}>
                  <i className="ri-price-tag-3-line text-2xl" style={{ color: '#7E887E' }}></i>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#7E887E' }}>Competitive Pricing</h3>
                <p className="text-gray-600 text-sm">Best market rates guaranteed</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: '#7E887E' }}>Project Details & Requirements</h2>
              
              <form id="construction-quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#7E887E' }}>Contact Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="your.email@company.com"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="Your company name"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-2">
                        Designation/Role
                      </label>
                      <input
                        type="text"
                        id="designation"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="Project Manager, Site Engineer, etc."
                      />
                    </div>
                  </div>
                </div>

                {/* Project Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#7E887E' }}>Project Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                        Project Type *
                      </label>
                      <div className="relative">
                        <select
                          id="projectType"
                          name="projectType"
                          value={formData.projectType}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 transition-colors"
                          style={{ focusRingColor: '#7E887E' }}
                          onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        >
                          <option value="">Select project type</option>
                          <option value="infrastructure">Infrastructure & Government</option>
                          <option value="residential">Residential Building</option>
                          <option value="commercial">Commercial Building</option>
                          <option value="industrial">Industrial Plant</option>
                          <option value="public-works">Public Works</option>
                          <option value="rmc">RMC Plant</option>
                          <option value="renovation">Renovation/Repair</option>
                          <option value="other">Other</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="projectLocation" className="block text-sm font-medium text-gray-700 mb-2">
                        Project Location *
                      </label>
                      <input
                        type="text"
                        id="projectLocation"
                        name="projectLocation"
                        value={formData.projectLocation}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="City, State"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="projectSize" className="block text-sm font-medium text-gray-700 mb-2">
                        Project Size
                      </label>
                      <div className="relative">
                        <select
                          id="projectSize"
                          name="projectSize"
                          value={formData.projectSize}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 transition-colors"
                          style={{ focusRingColor: '#7E887E' }}
                          onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        >
                          <option value="">Select project size</option>
                          <option value="small">Small (&lt; 1000 sq ft)</option>
                          <option value="medium">Medium (1000 - 10000 sq ft)</option>
                          <option value="large">Large (10000 - 50000 sq ft)</option>
                          <option value="mega">Mega (&gt; 50000 sq ft)</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                        Project Timeline
                      </label>
                      <div className="relative">
                        <select
                          id="timeline"
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 transition-colors"
                          style={{ focusRingColor: '#7E887E' }}
                          onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        >
                          <option value="">Select timeline</option>
                          <option value="immediate">Immediate (Within 1 week)</option>
                          <option value="1-month">Within 1 month</option>
                          <option value="3-months">Within 3 months</option>
                          <option value="6-months">Within 6 months</option>
                          <option value="planning">Still in planning phase</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Budget Range
                      </label>
                      <div className="relative">
                        <select
                          id="budget"
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 transition-colors"
                          style={{ focusRingColor: '#7E887E' }}
                          onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        >
                          <option value="">Select budget range</option>
                          <option value="under-1-lakh">Under ₹1 Lakh</option>
                          <option value="1-5-lakh">₹1 - 5 Lakh</option>
                          <option value="5-10-lakh">₹5 - 10 Lakh</option>
                          <option value="10-25-lakh">₹10 - 25 Lakh</option>
                          <option value="25-50-lakh">₹25 - 50 Lakh</option>
                          <option value="above-50-lakh">Above ₹50 Lakh</option>
                          <option value="discuss">Prefer to discuss</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chemical Requirements */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#7E887E' }}>Chemical Requirements</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="chemicalType" className="block text-sm font-medium text-gray-700 mb-2">
                        Chemical Type Required *
                      </label>
                      <div className="relative">
                        <select
                          id="chemicalType"
                          name="chemicalType"
                          value={formData.chemicalType}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 transition-colors"
                          style={{ focusRingColor: '#7E887E' }}
                          onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        >
                          <option value="">Select chemical type</option>
                          <option value="waterproofing">Waterproofing Solutions</option>
                          <option value="concrete-admixtures">Concrete Admixtures</option>
                          <option value="repair-rehabilitation">Repair & Rehabilitation</option>
                          <option value="protective-coatings">Protective Coatings</option>
                          <option value="flooring-systems">Flooring Systems</option>
                          <option value="sealants-adhesives">Sealants & Adhesives</option>
                          <option value="grouts-anchoring">Grouts & Anchoring</option>
                          <option value="multiple">Multiple Products</option>
                          <option value="consultation">Need Consultation</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="applicationArea" className="block text-sm font-medium text-gray-700 mb-2">
                        Application Area
                      </label>
                      <div className="relative">
                        <select
                          id="applicationArea"
                          name="applicationArea"
                          value={formData.applicationArea}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 transition-colors"
                          style={{ focusRingColor: '#7E887E' }}
                          onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        >
                          <option value="">Select application area</option>
                          <option value="basement">Basement/Foundation</option>
                          <option value="roof">Roof/Terrace</option>
                          <option value="bathroom">Bathroom/Wet Areas</option>
                          <option value="external-walls">External Walls</option>
                          <option value="structural">Structural Elements</option>
                          <option value="flooring">Flooring</option>
                          <option value="joints">Joints/Cracks</option>
                          <option value="entire-building">Entire Building</option>
                          <option value="other">Other</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Quantity
                      </label>
                      <input
                        type="text"
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="e.g., 500 sq ft, 10 bags, 200 liters"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700 mb-2">
                        Required Delivery Date
                      </label>
                      <input
                        type="date"
                        id="deliveryDate"
                        name="deliveryDate"
                        value={formData.deliveryDate}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Requirements */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#7E887E' }}>Additional Requirements</h3>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="specificRequirements" className="block text-sm font-medium text-gray-700 mb-2">
                        Specific Requirements/Challenges
                      </label>
                      <textarea
                        id="specificRequirements"
                        name="specificRequirements"
                        value={formData.specificRequirements}
                        onChange={handleInputChange}
                        maxLength={500}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm resize-vertical focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="Describe any specific challenges, environmental conditions, or special requirements..."
                      ></textarea>
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.specificRequirements.length}/500 characters
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="technicalSpecs" className="block text-sm font-medium text-gray-700 mb-2">
                        Technical Specifications
                      </label>
                      <textarea
                        id="technicalSpecs"
                        name="technicalSpecs"
                        value={formData.technicalSpecs}
                        onChange={handleInputChange}
                        maxLength={500}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm resize-vertical focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="Any specific technical requirements, standards, or certifications needed..."
                      ></textarea>
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.technicalSpecs.length}/500 characters
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="previousSupplier" className="block text-sm font-medium text-gray-700 mb-2">
                          Previous Supplier (if any)
                        </label>
                        <input
                          type="text"
                          id="previousSupplier"
                          name="previousSupplier"
                          value={formData.previousSupplier}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors"
                          style={{ focusRingColor: '#7E887E' }}
                          onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                          placeholder="Previous chemical supplier name"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="certificationNeeds" className="block text-sm font-medium text-gray-700 mb-2">
                          Certification Requirements
                        </label>
                        <div className="relative">
                          <select
                            id="certificationNeeds"
                            name="certificationNeeds"
                            value={formData.certificationNeeds}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 transition-colors"
                            style={{ focusRingColor: '#7E887E' }}
                            onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                          >
                            <option value="">Select certification needs</option>
                            <option value="iso">ISO Certification</option>
                            <option value="bis">BIS Standards</option>
                            <option value="green-building">Green Building Compliance</option>
                            <option value="fire-safety">Fire Safety Standards</option>
                            <option value="government">Government Project Compliance</option>
                            <option value="none">No specific requirements</option>
                            <option value="other">Other</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <i className="ri-arrow-down-s-line text-gray-400"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm resize-vertical focus:outline-none focus:ring-2 transition-colors"
                        style={{ focusRingColor: '#7E887E' }}
                        onFocus={(e) => e.target.style.borderColor = '#7E887E'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder="Any additional information or questions you'd like to share..."
                      ></textarea>
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.message.length}/500 characters
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-lg font-semibold"
                  style={{ backgroundColor: '#7E887E' }}
                  onMouseEnter={(e) => !isSubmitting && (e.target.style.backgroundColor = '#6B756B')}
                  onMouseLeave={(e) => !isSubmitting && (e.target.style.backgroundColor = '#7E887E')}
                >
                  {isSubmitting ? 'Submitting Quote Request...' : 'Get My Custom Quote'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-600 text-2xl mr-3"></i>
                      <div>
                        <h4 className="text-green-800 font-semibold">Quote Request Submitted Successfully!</h4>
                        <p className="text-green-700 mt-1">Thank you for your detailed requirements. Our technical team will review your project and send you a comprehensive quote within 24 hours. You'll also receive a confirmation email shortly.</p>
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 text-2xl mr-3"></i>
                      <div>
                        <h4 className="text-red-800 font-semibold">Submission Error</h4>
                        <p className="text-red-700 mt-1">Sorry, there was an error submitting your quote request. Please try again or contact us directly at +91 8130956009.</p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="mt-12 text-center">
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#7E887E' }}>Need Immediate Assistance?</h3>
              <p className="text-gray-600 mb-6">Our technical experts are available to discuss your requirements</p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-center justify-center space-x-3">
                  <i className="ri-phone-line text-2xl" style={{ color: '#7E887E' }}></i>
                  <div>
                    <p className="font-semibold">Call Us</p>
                    <p className="text-gray-600">+91 8130956009</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-center space-x-3">
                  <i className="ri-mail-line text-2xl" style={{ color: '#7E887E' }}></i>
                  <div>
                    <p className="font-semibold">Email Us</p>
                    <p className="text-gray-600">info@lavinenterprises.com</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-center space-x-3">
                  <i className="ri-time-line text-2xl" style={{ color: '#7E887E' }}></i>
                  <div>
                    <p className="font-semibold">Response Time</p>
                    <p className="text-gray-600">Within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}